// Increment 38: member-facing "report" control — raises a manual risk flag for
// admin review. Renders as an outline button that expands into a small form.
import { useState } from "react";
import { Flag, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const REASONS: Array<{ value: string; label: string }> = [
  { value: "scam", label: "Scam / fraud" },
  { value: "spam", label: "Spam" },
  { value: "abuse", label: "Abusive / inappropriate" },
  { value: "duplicate", label: "Duplicate" },
  { value: "payment_risk", label: "Payment risk" },
  { value: "other", label: "Something else" },
];

export default function ReportButton({
  targetType, targetId, targetUserId, className = "",
}: { targetType: string; targetId: string; targetUserId?: number; className?: string }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("scam");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!user) return null; // reporting requires a signed-in account

  async function submit() {
    setBusy(true); setErr(null);
    try {
      await api.reportFlag({ targetType, targetId, targetUserId, reason, detail: detail.trim() || undefined });
      setDone(true); setOpen(false);
    } catch (e: any) { setErr(e?.message || "Could not submit report."); }
    finally { setBusy(false); }
  }

  if (done) return <button disabled className={`btn-outline w-full text-brand-green ${className}`}><Check className="w-4 h-4" />Reported — thank you</button>;

  return (
    <div className={className}>
      <button onClick={() => setOpen((o) => !o)} className="btn-outline w-full text-mut" data-testid="report-toggle"><Flag className="w-4 h-4" />Report this listing</button>
      {open && (
        <div className="card p-3 mt-2 space-y-2">
          <label className="label">Reason</label>
          <select className="field" value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <textarea className="field" rows={2} placeholder="Add any detail (optional)" value={detail} onChange={(e) => setDetail(e.target.value)} />
          {err && <p className="text-xs text-brand-red">{err}</p>}
          <button onClick={submit} disabled={busy} className="btn-red w-full disabled:opacity-60" data-testid="report-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}Submit report
          </button>
        </div>
      )}
    </div>
  );
}
